import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api, type UserSession } from '@/api/client'

interface PlatformSession {
  access_token: string
  expires_in: number
  user: UserSession['user']
}

const TOKEN_KEY = 'fitness_platform_token'

export const usePlatformStore = defineStore('platform', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  const user = ref<PlatformSession['user'] | null>(null)
  const isAuthenticated = computed(() => Boolean(token.value))

  async function login(email: string, password: string): Promise<void> {
    const session = await api<PlatformSession>('/api/v1/platform/auth/login', {
      method: 'POST', body: JSON.stringify({ email, password }),
    })
    token.value = session.access_token
    user.value = session.user
    localStorage.setItem(TOKEN_KEY, session.access_token)
  }

  async function bootstrap(): Promise<boolean> {
    if (!token.value) return false
    if (user.value) return true
    try {
      user.value = await api<PlatformSession['user']>('/api/v1/platform/me', {}, token.value)
      return true
    } catch {
      logout()
      return false
    }
  }

  function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    return api<T>(path, options, token.value)
  }

  function logout(): void {
    token.value = ''
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
  }

  return { token, user, isAuthenticated, login, bootstrap, request, logout }
})
